import { useEffect, useMemo, useState } from "react";
import { Blocks, Loader2, Search, ChevronDown, ChevronUp, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { BlocksInputsEditor, describeInputs } from "./BlocksInputsEditor";

const API = "/api";

// Super Admin bulk tool: pick Python questions, review the inferred input schema, create Blocks copies
export function GenerateBlocksTool({ open, onOpenChange, onCreated }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [overrides, setOverrides] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [search, setSearch] = useState("");
  const [suitableOnly, setSuitableOnly] = useState(true);
  const [pool, setPool] = useState("all");
  const [keepDate, setKeepDate] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setSelected(new Set());
    setOverrides({});
    fetch(`${API}/admin/questions/blocks-candidates`, { credentials: "include" })
      .then((r) => r.json())
      .then(setData)
      .catch(() => toast.error("Could not load candidates"))
      .finally(() => setLoading(false));
  }, [open]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.candidates || []).filter((c) =>
      !c.has_blocks_version &&
      (!suitableOnly || c.suitable) &&
      (pool === "all" || c.pool === pool) &&
      (!q || c.title.toLowerCase().includes(q))
    );
  }, [data, search, suitableOnly, pool]);

  const inputsFor = (c) => overrides[c.question_id] ?? c.inputs;
  const toggle = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectVisible = (n) => setSelected(new Set(rows.slice(0, n).map((c) => c.question_id)));

  const create = async () => {
    if (!selected.size) return;
    setCreating(true);
    try {
      const inputs_overrides = {};
      selected.forEach((id) => { if (overrides[id]) inputs_overrides[id] = overrides[id]; });
      const r = await fetch(`${API}/admin/questions/generate-blocks`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ question_ids: [...selected], inputs_overrides, keep_date: keepDate }),
      });
      const res = await r.json();
      if (!r.ok) throw new Error(res.detail || "Failed");
      toast.success(`Created ${res.created.length} Blocks question${res.created.length === 1 ? "" : "s"}${res.skipped.length ? ` (${res.skipped.length} skipped)` : ""}`);
      onCreated?.(res);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="generate-blocks-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Blocks className="w-5 h-5 text-sky-500" /> Generate Blocks versions</DialogTitle>
          <DialogDescription>
            Copies Python questions into the Blocks track. Each copy keeps the tests and sample solution; check the detected inputs before creating.
            {data && <span className="ml-1 font-medium text-foreground" data-testid="generate-blocks-suitable-count">{data.suitable} suitable of {data.total}.</span>}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search titles…" className="pl-8 h-9" data-testid="generate-blocks-search" />
          </div>
          <select value={pool} onChange={(e) => setPool(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm" data-testid="generate-blocks-pool-filter">
            <option value="all">All pools</option>
            <option value="daily">Warm Up</option>
            <option value="challenge">Challenge</option>
          </select>
          <label className="inline-flex items-center gap-1.5 text-sm"><input type="checkbox" checked={suitableOnly} onChange={(e) => setSuitableOnly(e.target.checked)} data-testid="generate-blocks-suitable-only" /> Suitable only</label>
          <Button variant="outline" size="sm" onClick={() => selectVisible(10)} data-testid="generate-blocks-select-10">Select first 10</Button>
          <Button variant="outline" size="sm" onClick={() => selectVisible(30)} data-testid="generate-blocks-select-30">Select first 30</Button>
          <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())} data-testid="generate-blocks-clear">Clear</Button>
        </div>

        <div className="flex-1 overflow-y-auto border border-border rounded-xl" data-testid="generate-blocks-list">
          {loading ? (
            <div className="p-10 text-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Analysing questions…</div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-muted-foreground">No matching questions.</div>
          ) : rows.map((c) => (
            <div key={c.question_id} className="border-b border-border last:border-b-0" data-testid={`generate-blocks-row-${c.question_id}`}>
              <div className="flex items-center gap-3 px-3 py-2 hover:bg-muted/30">
                <input type="checkbox" checked={selected.has(c.question_id)} onChange={() => toggle(c.question_id)} data-testid={`generate-blocks-check-${c.question_id}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium truncate">{c.title}</span>
                    <Badge variant="outline" className={c.pool === "challenge" ? "text-secondary border-secondary" : "text-primary border-primary"}>{c.pool === "challenge" ? "Challenge" : "Warm Up"}</Badge>
                    {c.date && <Badge variant="secondary" className="text-xs">{c.date}</Badge>}
                    {c.suitable ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <span className="inline-flex items-center gap-1 text-xs text-amber-600"><AlertTriangle className="w-3.5 h-3.5" />{c.reason}</span>}
                  </div>
                  <p className="text-xs text-muted-foreground font-mono truncate">{describeInputs(inputsFor(c))} · {c.solution_lines} lines · {c.test_case_count} tests</p>
                </div>
                <Button variant="ghost" size="sm" className="h-8 gap-1" onClick={() => setExpanded(expanded === c.question_id ? null : c.question_id)} data-testid={`generate-blocks-edit-${c.question_id}`}>
                  Inputs {expanded === c.question_id ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </Button>
              </div>
              {expanded === c.question_id && (
                <div className="px-3 pb-3 pl-9 grid md:grid-cols-2 gap-4">
                  <BlocksInputsEditor compact inputs={inputsFor(c)} onChange={(inputs) => setOverrides((o) => ({ ...o, [c.question_id]: inputs }))} testId={`generate-blocks-inputs-${c.question_id}`} />
                  <div className="text-xs">
                    <p className="text-muted-foreground mb-1">Example input → output</p>
                    <pre className="bg-muted rounded p-2 whitespace-pre-wrap">{c.example_input || "(none)"}</pre>
                    <pre className="bg-muted rounded p-2 mt-1 whitespace-pre-wrap">{c.example_output || "(none)"}</pre>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
          <label className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
            <input type="checkbox" checked={keepDate} onChange={(e) => setKeepDate(e.target.checked)} data-testid="generate-blocks-keep-date" />
            Keep annual schedule date (otherwise copies are always available)
          </label>
          <Button onClick={create} disabled={creating || !selected.size} className="gap-2 rounded-xl" data-testid="generate-blocks-create-btn">
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Blocks className="w-4 h-4" />}
            Create {selected.size} Blocks version{selected.size === 1 ? "" : "s"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
